/**
 * @file google-workspace.service.ts
 * @version 2.0.0
 * @datetime 2026-09-08T20:58:00+03:00
 * @description Manages Google Workspace permissions and automatic 'ustat_muhasebe-sistemi' Google Drive directory verification.
 */

import { Injectable, signal } from '@angular/core';
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  User,
  onAuthStateChanged,
} from 'firebase/auth';
import { environment } from '../../environments/environment';

export interface WorkspacePermissionItem {
  key: 'gmail' | 'drive' | 'calendar' | 'sheets' | 'docs' | 'tasks';
  title: string;
  scope: string;
  description: string;
  icon: string;
  granted: boolean;
}

export interface CalendarEventItem {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  time?: string; // HH:mm
  description?: string;
  type: 'tax' | 'appointment' | 'deadline' | 'meeting';
  googleSynced?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleWorkspaceService {
  readonly version = '2.0.0';

  private auth: any = null;
  readonly currentUser = signal<User | null>(null);
  readonly isAuthenticating = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);
  readonly googleAccessToken = signal<string | null>(null);
  readonly ustatDriveFolderId = signal<string | null>(null);

  readonly permissions = signal<WorkspacePermissionItem[]>([
    {
      key: 'gmail',
      title: 'Gmail',
      scope: 'https://www.googleapis.com/auth/gmail.modify',
      description: 'Müşteri e-postaları ve e-fatura bildirimlerini yönetmek için.',
      icon: 'mail-outline',
      granted: false,
    },
    {
      key: 'drive',
      title: 'Google Drive',
      scope: 'https://www.googleapis.com/auth/drive.file',
      description: 'Fatura PDF ve evrak yedeklerini güvenle saklamak için.',
      icon: 'cloud-upload-outline',
      granted: false,
    },
    {
      key: 'calendar',
      title: 'Google Calendar',
      scope: 'https://www.googleapis.com/auth/calendar',
      description: 'Ödeme ve tahsilat vadelerini takvime otomatik eklemek için.',
      icon: 'calendar-outline',
      granted: false,
    },
    {
      key: 'sheets',
      title: 'Google Sheets',
      scope: 'https://www.googleapis.com/auth/spreadsheets',
      description: 'Hesap raporlarını ve müşteri listelerini tabloya aktarmak için.',
      icon: 'grid-outline',
      granted: false,
    },
    {
      key: 'docs',
      title: 'Google Docs',
      scope: 'https://www.googleapis.com/auth/documents',
      description: 'Teklif ve sözleşme metinleri oluşturmak için.',
      icon: 'document-text-outline',
      granted: false,
    },
    {
      key: 'tasks',
      title: 'Google Tasks',
      scope: 'https://www.googleapis.com/auth/tasks',
      description: 'Günlük iş ve teslimat görevlerini listelemek için.',
      icon: 'checkbox-outline',
      granted: false,
    },
  ]);

  constructor() {
    this.initFirebase();
  }

  private initFirebase(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const cachedToken = localStorage.getItem('google_access_token');
        if (cachedToken) this.googleAccessToken.set(cachedToken);
        const cachedFolderId = localStorage.getItem('ustat_drive_folder_id');
        if (cachedFolderId) this.ustatDriveFolderId.set(cachedFolderId);
      }

      if (!getApps().length) {
        initializeApp(environment.firebaseConfig);
      }
      this.auth = getAuth();
      onAuthStateChanged(this.auth, (user) => {
        this.currentUser.set(user);
        if (user) {
          console.log('[Ustat][GoogleWorkspace] Google user authenticated:', user.email);
          this.permissions.update((items) =>
            items.map((item) => ({ ...item, granted: true }))
          );
        } else {
          this.permissions.update((items) =>
            items.map((item) => ({ ...item, granted: false }))
          );
        }
      });
    } catch (err) {
      console.warn('[Ustat][GoogleWorkspace] Firebase initialization notice:', err);
    }
  }

  /**
   * Triggers the Google OAuth consent flow with requested Workspace scopes.
   */
  async requestAllPermissions(): Promise<boolean> {
    if (!this.auth) {
      console.error('[Ustat][GoogleWorkspace] Firebase Auth not initialized');
      this.errorMessage.set('Firebase Auth başlatılamadı.');
      return false;
    }

    this.isAuthenticating.set(true);
    this.errorMessage.set(null);

    const provider = new GoogleAuthProvider();

    // Add all 6 Workspace scopes
    for (const item of this.permissions()) {
      provider.addScope(item.scope);
    }

    // Force prompt for consent
    provider.setCustomParameters({
      prompt: 'consent',
      access_type: 'offline',
    });

    try {
      console.log('[Ustat][GoogleWorkspace] Launching Google OAuth popup with scopes...');
      const credential = await signInWithPopup(this.auth, provider);
      const user = credential.user;
      this.currentUser.set(user);

      const oauthCred = GoogleAuthProvider.credentialFromResult(credential);
      const accessToken = oauthCred?.accessToken;
      if (accessToken) {
        this.googleAccessToken.set(accessToken);
        if (typeof window !== 'undefined') {
          localStorage.setItem('google_access_token', accessToken);
        }
        await this.ensureUstatDriveFolder(accessToken);
      }

      console.log('[Ustat][GoogleWorkspace] Successfully authorized user:', user.email);
      this.permissions.update((items) =>
        items.map((item) => ({ ...item, granted: true }))
      );
      this.isAuthenticating.set(false);
      return true;
    } catch (err: any) {
      console.error('[Ustat][GoogleWorkspace] Authorization error:', err);
      this.errorMessage.set(err?.message || 'İzin alma işlemi iptal edildi veya başarısız oldu.');
      this.isAuthenticating.set(false);
      return false;
    }
  }

  /**
   * Checks Google Drive for directory 'ustat_muhasebe-sistemi'.
   * If not present, creates the directory.
   */
  async ensureUstatDriveFolder(token?: string): Promise<{ folderId: string; created: boolean } | null> {
    const accessToken = token || this.googleAccessToken() || (typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null);
    if (!accessToken) {
      console.log('[Ustat][GoogleDrive] No Google OAuth access token available yet to check Drive folder.');
      return null;
    }

    const folderName = 'hesaplar';
    console.log(`[Ustat][GoogleDrive] Checking Google Drive for '${folderName}' directory...`);

    try {
      // 1. Search if directory exists
      const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(`name = '${folderName}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`)}&fields=files(id,name,mimeType)`;
      const searchRes = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });

      if (searchRes.ok) {
        const searchData = await searchRes.json();
        const existing = searchData.files && searchData.files.length > 0 ? searchData.files[0] : null;

        if (existing && existing.id) {
          console.log(`[Ustat][GoogleDrive] '${folderName}' directory exists with ID: ${existing.id}`);
          this.ustatDriveFolderId.set(existing.id);
          if (typeof window !== 'undefined') {
            localStorage.setItem('ustat_drive_folder_id', existing.id);
          }
          return { folderId: existing.id, created: false };
        }
      } else {
        const errText = await searchRes.text();
        console.warn('[Ustat][GoogleDrive] Search folder notice:', searchRes.status, errText);
      }

      // 2. Directory does not exist -> Create it
      console.log(`[Ustat][GoogleDrive] '${folderName}' directory NOT found. Creating directory...`);
      const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: folderName,
          mimeType: 'application/vnd.google-apps.folder',
          description: 'U-Stat Muhasebe Sistemi Hesaplar Klasörü',
        }),
      });

      if (createRes.ok) {
        const createdData = await createRes.json();
        console.log(`[Ustat][GoogleDrive] SUCCESS! '${folderName}' directory created with ID: ${createdData.id}`);
        this.ustatDriveFolderId.set(createdData.id);
        if (typeof window !== 'undefined') {
          localStorage.setItem('ustat_drive_folder_id', createdData.id);
        }
        return { folderId: createdData.id, created: true };
      } else {
        const errText = await createRes.text();
        console.error('[Ustat][GoogleDrive] Error creating folder in Google Drive:', createRes.status, errText);
        return null;
      }
    } catch (err: any) {
      console.error('[Ustat][GoogleDrive] Exception while ensuring Drive directory:', err);
      return null;
    }
  }

  /**
   * Finds or creates a folder by name inside a parent folder (or Drive root if parentId is undefined).
   */
  async findOrCreateFolder(name: string, parentId?: string, token?: string): Promise<string | null> {
    const accessToken = token || this.googleAccessToken() || (typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null);
    if (!accessToken) {
      console.warn('[Ustat][GoogleDrive] No Google OAuth access token available.');
      return null;
    }

    try {
      let query = `name = '${name}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
      if (parentId) {
        query += ` and '${parentId}' in parents`;
      }
      const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType)`;
      const searchRes = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });

      if (searchRes.ok) {
        const searchData = await searchRes.json();
        if (searchData.files && searchData.files.length > 0) {
          console.log(`[Ustat][GoogleDrive] Folder '${name}' found with ID: ${searchData.files[0].id}`);
          return searchData.files[0].id;
        }
      }

      // Not found -> create folder
      console.log(`[Ustat][GoogleDrive] Creating folder '${name}' under parent: ${parentId || 'root'}...`);
      const bodyPayload: any = {
        name,
        mimeType: 'application/vnd.google-apps.folder',
      };
      if (parentId) {
        bodyPayload.parents = [parentId];
      }

      const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(bodyPayload),
      });

      if (createRes.ok) {
        const createdData = await createRes.json();
        console.log(`[Ustat][GoogleDrive] Folder '${name}' created with ID: ${createdData.id}`);
        return createdData.id;
      } else {
        const errText = await createRes.text();
        console.error(`[Ustat][GoogleDrive] Failed creating folder '${name}':`, createRes.status, errText);
        return null;
      }
    } catch (err) {
      console.error(`[Ustat][GoogleDrive] Exception in findOrCreateFolder('${name}'):`, err);
      return null;
    }
  }

  /**
   * Ensures a nested folder path exists in Google Drive (e.g. ['hesaplar', vkn, 'data']).
   * If rootParentId is not provided, defaults to ustatDriveFolderId if available.
   */
  async ensureDriveFolderPath(segments: string[], rootParentId?: string, token?: string): Promise<string | null> {
    let currentParentId = rootParentId ?? (this.ustatDriveFolderId() || undefined);
    for (const segment of segments) {
      if (!segment) continue;
      const folderId = await this.findOrCreateFolder(segment, currentParentId, token);
      if (!folderId) {
        console.error(`[Ustat][GoogleDrive] Failed ensuring folder segment: ${segment}`);
        return null;
      }
      currentParentId = folderId;
    }
    return currentParentId || null;
  }

  /**
   * Saves or updates yevmiye journal transactions to Google Drive as a Google Sheet named 'yevmiye'
   * at directory path 'hesaplar/[vkn]/data'.
   */
  async saveYevmiyeToGoogleSheet(
    vkn: string,
    entries: any[],
    token?: string
  ): Promise<{ spreadsheetId: string; webViewLink: string; rowCount: number } | null> {
    const accessToken = token || this.googleAccessToken() || (typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null);
    if (!accessToken) {
      console.warn('[Ustat][GoogleDrive] saveYevmiyeToGoogleSheet: Google OAuth token missing.');
      return null;
    }

    try {
      // 1. First ensure base hesaplar folder if not yet verified
      if (!this.ustatDriveFolderId()) {
        await this.ensureUstatDriveFolder(accessToken);
      }

      // 2. Ensure nested directories under hesaplar/[vkn]: data, docs, dosyalar, evrak, ortak
      const vknFolderId = await this.ensureDriveFolderPath([vkn], this.ustatDriveFolderId() || undefined, accessToken);
      if (!vknFolderId) {
        throw new Error(`Google Drive üzerinde 'hesaplar/${vkn}' dizini oluşturulamadı.`);
      }

      await Promise.allSettled([
        this.findOrCreateFolder('data', vknFolderId, accessToken),
        this.findOrCreateFolder('docs', vknFolderId, accessToken),
        this.findOrCreateFolder('dosyalar', vknFolderId, accessToken),
        this.findOrCreateFolder('evrak', vknFolderId, accessToken),
        this.findOrCreateFolder('ortak', vknFolderId, accessToken),
      ]);

      const targetFolderId = await this.findOrCreateFolder('data', vknFolderId, accessToken);
      if (!targetFolderId) {
        throw new Error(`Google Drive üzerinde 'hesaplar/${vkn}/data' dizini oluşturulamadı.`);
      }

      // 3. Search for existing Google Sheet named 'yevmiye' in target folder
      const fileQuery = `name = 'yevmiye' and mimeType = 'application/vnd.google-apps.spreadsheet' and '${targetFolderId}' in parents and trashed = false`;
      const searchRes = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(fileQuery)}&fields=files(id,name,webViewLink)`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });

      let spreadsheetId: string | null = null;
      let webViewLink = '';

      if (searchRes.ok) {
        const searchData = await searchRes.json();
        if (searchData.files && searchData.files.length > 0) {
          spreadsheetId = searchData.files[0].id;
          webViewLink = searchData.files[0].webViewLink || `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
          console.log(`[Ustat][GoogleDrive] Existing 'yevmiye' sheet found with ID: ${spreadsheetId}`);
        }
      }

      // 4. Create new Google Sheet if not found
      if (!spreadsheetId) {
        console.log(`[Ustat][GoogleDrive] Creating new 'yevmiye' spreadsheet in folder ${targetFolderId}...`);
        const createRes = await fetch('https://www.googleapis.com/drive/v3/files?fields=id,name,webViewLink', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            name: 'yevmiye',
            mimeType: 'application/vnd.google-apps.spreadsheet',
            parents: [targetFolderId],
            description: `${vkn} Nolu Mükellef Yevmiye Defteri ve Fiş Hareketleri`,
          }),
        });

        if (!createRes.ok) {
          const errText = await createRes.text();
          throw new Error(`Google Sheet oluşturulamadı: ${errText}`);
        }

        const createdSheet = await createRes.json();
        spreadsheetId = createdSheet.id;
        webViewLink = createdSheet.webViewLink || `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
        console.log(`[Ustat][GoogleDrive] 'yevmiye' spreadsheet successfully created with ID: ${spreadsheetId}`);
      }

      // 5. Build table rows (Header + Data)
      const headers = [
        'Fiş No',
        'Madde / Sıra No',
        'Yevmiye Tarihi',
        'Belge Türü',
        'Belge No',
        'Belge Tarihi',
        'Hesap Kodu',
        'Ana Hesap',
        'Hesap Adı',
        'Sınıflandırma',
        'Açıklama',
        'Borç (₺)',
        'Alacak (₺)',
        'Döviz Cinsi',
        'Döviz Borç',
        'Döviz Alacak',
        'Döviz Kuru',
        'Karşı Hesap No',
        'Karşı Hesap Adı',
        'Karşı Taraf (Cari)',
        'Karşı Taraf VKN/TCKN',
        'Ödeme Yöntemi',
        'Belge Ref',
        'Kayıt ID',
      ];

      const rows = entries.map((e) => [
        e.voucherNumber || e.entryNumber || '',
        e.sequenceNumber ?? (e.orderNumber ?? ''),
        e.entryDate || e.documentDate || e.date || '',
        e.documentType || '',
        e.documentNumber || '',
        e.documentDate || '',
        e.accountCode || '',
        e.main_account_name || e.main_account_code || e.accountLevel1 || '',
        e.accountName || '',
        e.classification || '',
        e.description || '',
        Number(e.debit) || 0,
        Number(e.credit) || 0,
        e.currency || 'TRY',
        Number(e.currency_debit || e.debitUsd || e.debitEur || 0),
        Number(e.currency_credit || e.creditUsd || e.creditEur || 0),
        e.currency_rate || e.document_rate || e.exchangeRateUsd || e.exchangeRateEur || '',
        e.counter_account_no || e.counter_main_account_code || '',
        e.counter_main_account_name || '',
        e.counterpartName || e.counterpartTitle || '',
        e.counterpartTaxId || '',
        e.paymentMethod || '',
        e.doc_ref || '',
        e._id || '',
      ]);

      const values = [headers, ...rows];

      // 6. Write values to Google Sheets via Sheets API v4
      const updateUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/A1?valueInputOption=USER_ENTERED`;
      const updateRes = await fetch(updateUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          range: 'A1',
          majorDimension: 'ROWS',
          values,
        }),
      });

      if (!updateRes.ok) {
        const errText = await updateRes.text();
        console.warn('[Ustat][GoogleDrive] Sheets update values notice:', updateRes.status, errText);
      } else {
        console.log(`[Ustat][GoogleDrive] Successfully wrote ${values.length} rows to 'yevmiye' sheet.`);
      }

      // 7. Format Header Row (Freeze row 1 and style header background)
      try {
        await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            requests: [
              {
                updateSheetProperties: {
                  properties: {
                    sheetId: 0,
                    gridProperties: { frozenRowCount: 1 },
                  },
                  fields: 'gridProperties.frozenRowCount',
                },
              },
              {
                repeatCell: {
                  range: {
                    sheetId: 0,
                    startRowIndex: 0,
                    endRowIndex: 1,
                  },
                  cell: {
                    userEnteredFormat: {
                      backgroundColor: { red: 0.11, green: 0.44, blue: 0.42 },
                      textFormat: { bold: true, foregroundColor: { red: 1, green: 1, blue: 1 } },
                    },
                  },
                  fields: 'userEnteredFormat(backgroundColor,textFormat)',
                },
              },
            ],
          }),
        });
      } catch (styleErr) {
        console.warn('[Ustat][GoogleDrive] Header styling skipped/optional:', styleErr);
      }

      if (!spreadsheetId) {
        throw new Error('Google Spreadsheet ID alınamadı.');
      }

      return {
        spreadsheetId,
        webViewLink,
        rowCount: entries.length,
      };
    } catch (err: any) {
      console.error('[Ustat][GoogleDrive] Error in saveYevmiyeToGoogleSheet:', err);
      return null;
    }
  }

  /**
   * Uploads a document directly to Google Drive under 'hesaplar/[vkn]/docs/[fileName]'
   * e.g. [vkn]_yevmiye.xlsx
   */
  async uploadFileToDriveDocs(
    vkn: string,
    file: File | Blob,
    fileName: string,
    token?: string
  ): Promise<string | null> {
    const accessToken = token || this.googleAccessToken() || (typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null);
    if (!accessToken) {
      console.warn('[Ustat][GoogleDrive] uploadFileToDriveDocs: Google OAuth access token missing.');
      return null;
    }

    try {
      if (!this.ustatDriveFolderId()) {
        await this.ensureUstatDriveFolder(accessToken);
      }

      const vknFolderId = await this.ensureDriveFolderPath([vkn], this.ustatDriveFolderId() || undefined, accessToken);
      if (!vknFolderId) {
        throw new Error(`Google Drive üzerinde 'hesaplar/${vkn}' dizini bulunamadı.`);
      }

      // Ensure all 5 directories exist under hesaplar/[vkn]
      await Promise.allSettled([
        this.findOrCreateFolder('data', vknFolderId, accessToken),
        this.findOrCreateFolder('docs', vknFolderId, accessToken),
        this.findOrCreateFolder('dosyalar', vknFolderId, accessToken),
        this.findOrCreateFolder('evrak', vknFolderId, accessToken),
        this.findOrCreateFolder('ortak', vknFolderId, accessToken),
      ]);

      const docsFolderId = await this.findOrCreateFolder('docs', vknFolderId, accessToken);
      if (!docsFolderId) {
        throw new Error(`Google Drive üzerinde 'hesaplar/${vkn}/docs' dizini oluşturulamadı.`);
      }

      // Check if file already exists in docs folder
      const existQuery = `name = '${fileName.replace(/'/g, "\\'")}' and '${docsFolderId}' in parents and trashed = false`;
      const existRes = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(existQuery)}&fields=files(id)`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      let existingFileId: string | null = null;
      if (existRes.ok) {
        const existData = await existRes.json();
        if (existData.files && existData.files.length > 0) {
          existingFileId = existData.files[0].id;
        }
      }

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify({
        name: fileName,
        parents: existingFileId ? undefined : [docsFolderId],
      })], { type: 'application/json' }));
      form.append('file', file);

      const endpoint = existingFileId
        ? `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart`
        : `https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart`;

      const uploadRes = await fetch(endpoint, {
        method: existingFileId ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form,
      });

      if (uploadRes.ok) {
        const upJson = await uploadRes.json();
        console.log(`[Ustat][GoogleDrive] Successfully uploaded '${fileName}' to 'hesaplar/${vkn}/docs' (ID: ${upJson.id})`);
        return upJson.id;
      } else {
        const errText = await uploadRes.text();
        console.warn(`[Ustat][GoogleDrive] Upload warning for '${fileName}':`, errText);
        return null;
      }
    } catch (e: any) {
      console.warn('[Ustat][GoogleDrive] Error uploading file to docs folder:', e?.message || e);
      return null;
    }
  }

  /**
   * Ensures the standard directory hierarchy in Google Drive under 'hesaplar':
   * hesaplar/[smmmVkn]/[mukellefVkn]/ (data, docs, dosyalar, evrak, ortak)
   */
  async ensureCompanyDriveHierarchy(smmmVkn: string, mukellefVkn?: string, token?: string): Promise<void> {
    if (!smmmVkn) return;
    const rootFolderId = this.ustatDriveFolderId();
    if (!rootFolderId) return;

    try {
      // 1. Ensure smmmVkn folder under 'hesaplar'
      const smmmFolderId = await this.findOrCreateFolder(smmmVkn, rootFolderId, token);
      if (!smmmFolderId) return;

      const subDirs = ['data', 'docs', 'dosyalar', 'evrak', 'ortak'];
      for (const sub of subDirs) {
        await this.findOrCreateFolder(sub, smmmFolderId, token);
      }

      // 2. If mukellefVkn is specified and distinct, ensure [smmmVkn]/[mukellefVkn]/...
      if (mukellefVkn && mukellefVkn !== smmmVkn) {
        const mukFolderId = await this.findOrCreateFolder(mukellefVkn, smmmFolderId, token);
        if (mukFolderId) {
          for (const sub of subDirs) {
            await this.findOrCreateFolder(sub, mukFolderId, token);
          }
        }
      }
      console.log(`[Ustat][GoogleDrive] Company directory hierarchy verified under 'hesaplar/${smmmVkn}'`);
    } catch (err) {
      console.warn('[Ustat][GoogleDrive] Notice during directory hierarchy setup:', err);
    }
  }

  /**
   * Called automatically when user logs into the app.
   */
  async onUserLoggedIn(vkn?: string): Promise<void> {
    console.log('[Ustat][GoogleWorkspace] User logged in event received. Verifying Google Drive "hesaplar" directory...');
    const token = this.googleAccessToken() || (typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null);
    if (token) {
      const res = await this.ensureUstatDriveFolder(token);
      if (res && res.folderId && vkn) {
        await this.ensureCompanyDriveHierarchy(vkn, undefined, token);
      }
    } else {
      console.log('[Ustat][GoogleWorkspace] Notice: Google access token not yet linked. "hesaplar" directory will be verified once Google account is linked/authorized.');
    }
  }

  readonly calendarEvents = signal<CalendarEventItem[]>([]);

  /**
   * Adds a new calendar event / appointment and saves it locally.
   */
  addCalendarEvent(item: Omit<CalendarEventItem, 'id'>): CalendarEventItem {
    const newEvent: CalendarEventItem = {
      ...item,
      id: 'cal_' + Date.now(),
      googleSynced: false,
    };

    this.calendarEvents.update((events) => [newEvent, ...events]);
    this.saveEventsToStorage();
    return newEvent;
  }

  /**
   * Removes a calendar event by ID.
   */
  deleteCalendarEvent(id: string): void {
    this.calendarEvents.update((events) => events.filter((e) => e.id !== id));
    this.saveEventsToStorage();
  }

  /**
   * Generates a direct Google Calendar web/app intent link to open or add the event.
   */
  generateGoogleCalendarUrl(event: CalendarEventItem): string {
    const title = encodeURIComponent(event.title);
    const details = encodeURIComponent(event.description || 'Ustat Mobil İş Asistanı Hatırlatması');
    const location = encodeURIComponent('Kadıköy, İstanbul');

    // Format ISO string YYYYMMDDTHHMMSSZ
    const dateClean = event.date.replace(/-/g, '');
    const timeClean = (event.time || '10:00').replace(':', '') + '00';
    const startIso = `${dateClean}T${timeClean}`;
    // Add 1 hour duration
    const endIso = `${dateClean}T${(parseInt(timeClean.slice(0, 2), 10) + 1).toString().padStart(2, '0')}${timeClean.slice(2)}`;

    return `https://calendar.google.com/calendar/r/eventedit?text=${title}&dates=${startIso}/${endIso}&details=${details}&location=${location}`;
  }

  /**
   * Syncs an event to Google Calendar via Google Calendar API if authenticated,
   * or opens the Google Calendar intent.
   */
  async syncEventToGoogle(event: CalendarEventItem): Promise<boolean> {
    const url = this.generateGoogleCalendarUrl(event);
    window.open(url, '_blank');
    this.calendarEvents.update((events) =>
      events.map((e) => (e.id === event.id ? { ...e, googleSynced: true } : e))
    );
    this.saveEventsToStorage();
    return true;
  }

  private saveEventsToStorage(): void {
    try {
      localStorage.setItem('ustat_calendar_events', JSON.stringify(this.calendarEvents()));
    } catch (e) {}
  }

  async logout(): Promise<void> {
    if (this.auth) {
      await signOut(this.auth);
      this.currentUser.set(null);
      this.permissions.update((items) =>
        items.map((item) => ({ ...item, granted: false }))
      );
    }
  }
}
